/**
 * FirmBatchDetail — read-only view of a single batch's codes.
 * Client display is `First L.` only — full names and medical data are
 * NOT exposed to firm staff (enforced server-side + rendered here).
 */

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiV2, downloadCsvV2 } from "@/lib/apiV2";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "@phosphor-icons/react";

const STATUS_BADGE = {
  available: { text: "Available", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  redeemed:  { text: "Redeemed",  cls: "bg-sky-50 text-sky-700 border-sky-200" },
  void:      { text: "Void",      cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function FirmBatchDetail() {
  const { batchId } = useParams();

  const codes = useQuery({
    queryKey: ["firm-batch-codes", batchId],
    queryFn: async () => (await apiV2.get(`/firm/batches/${batchId}/codes`)).data,
  });

  const onDownloadUnused = async () => {
    try {
      await downloadCsvV2(
        `/firm/batches/${batchId}/unused.csv`,
        `batch-${batchId.slice(0, 8)}-unused.csv`,
      );
    } catch (e) {
      toast.error(e.message);
    }
  };

  const rows = codes.data?.codes || [];
  const availableCount = rows.filter((r) => r.status === "available").length;

  return (
    <div className="space-y-6" data-testid="firm-batch-detail">
      <div>
        <Link to="/firm" className="text-sm text-[#2C4C3B] flex items-center gap-1 no-underline">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="mt-2 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-[#2C4C3B]">Batch codes</h1>
            <p className="text-sm text-slate-500">
              {rows.length} total · {availableCount} available for handout
            </p>
          </div>
          {availableCount > 0 && (
            <Button
              variant="outline"
              onClick={onDownloadUnused}
              data-testid="firm-download-unused"
            >
              <Download size={16} className="mr-1" /> Download unused (CSV)
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-x-auto" data-testid="firm-code-table">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Code</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Redeemed</th>
              <th className="text-left px-4 py-3 font-medium">Client</th>
              <th className="text-left px-4 py-3 font-medium">Paid until</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {codes.isLoading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No codes.</td></tr>
            ) : (
              rows.map((r) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.available;
                return (
                  <tr key={r.code} data-testid={`code-row-${r.code}`}>
                    <td className="px-4 py-3 font-mono text-slate-900">{r.code}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${badge.cls}`}>
                        {badge.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.client_display || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {r.paid_until || "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-slate-400 max-w-2xl">
        Privacy: for redeemed codes we show the client's first name and last
        initial only. Full names, emails, medical trips and receipts are never
        visible to firm staff.
      </p>
    </div>
  );
}
